import { getRowSelectionColumn } from "./getRowSelectionColumn";
import { getActionColumn } from "./getActionColumn";
import Header from "../components/common/table/Header";
import Cell from "../components/common/table/Cell";
import { getSubrowToggleColumn } from "./getSubrowToggleColumn";

export const generateDynamicColumns = ({
  columnHelper,
  headerData,
  totals = {},
  actionConfig = {},
  navigationConfig = {},
  filterConfig = {},
  cellEventHandlers = {},
  editableFields = {},
  columnTypes = {
    rowSelectionColumn: false,
    actionsColumn: true,
  },
  enableSubrows = false,
}) => {
  const getColumnConfig = (key, columnInfo) => {
    const getDefaultFilterType = (columnType) => {
      switch (columnType) {
        case "float":
        case "currency":
        case "int":
        case "integer":
          return "range";
        case "bool":
        case "launch_status":
          return "select";
        case "str":
        case "long_text":
        case "uri":
        case "url":
          return "text";
        case "date":
        case "datetime":
          return "date_range";
        case "rich_dropdown":
        case "dropdown":
          return "dropdown";
        case "files":
        case "image":
          return null; // These types don't typically have filters
        default:
          return null;
      }
    };

    // For client-side filtering, never use backend's filter_type
    // Use filterConfig override first, then determine based on column type
    const filterType =
      filterConfig?.filterTypes?.[key] || getDefaultFilterType(columnInfo.type);

    const baseConfig = {
      id: key,
      header: () => (
        <Header columnInfo={{ ...columnInfo, key }} totals={totals} />
      ),
      enableColumnFilter:
        !filterConfig?.excludeFromFilter?.includes(key) && filterType !== null,
      // Column sizing configuration for TanStack Table resizing
      size: columnInfo.width || 150, // Default size of 150px, use width from columnInfo if available
      minSize: columnInfo.min_width || 80, // Minimum size of 80px
      maxSize: columnInfo.max_width || 500, // Maximum size of 500px
      enableResizing: true, // Enable resizing for this column
      meta: {
        // columnName can be accessed as columnInfo.display
        columnInfo: { ...columnInfo, key },
        // this is done to apply custom padding based on whether we want the whole cell to be clickable or not
        // search "px-6 py-2" in the related files to see where this is applied
        navigable: navigationConfig[key]?.navigable || false,
        clickable: cellEventHandlers[key]?.onClick ? true : false,
        handlers: {
          onClick: cellEventHandlers[key]?.onClick || (() => false),
        },
        // Add card-specific metadata
        isImageColumn: columnInfo.type === "image",
        excludeFromCard: ["id", "uri"].includes(key),
        filterType,
      },
      cell: (info) => (
        <Cell
          info={info}
          columnInfo={{ ...columnInfo, key }}
          editConfig={editableFields[key] || {}}
          navigationConfig={navigationConfig[key] || {}}
          headerData={headerData}
        />
      ),
    };

    const typeConfigs = {
      rich_dropdown: {
        enableGlobalFilter: false,
        enableSorting: false,
        enableColumnFilter: true,
      },
      url: {
        enableGlobalFilter: false,
        enableSorting: false,
        enableColumnFilter: true,
      },
      dropdown: {
        enableGlobalFilter: false,
        enableSorting: false,
        enableColumnFilter: true,
      },
      files: {
        enableGlobalFilter: false,
        enableSorting: false,
      },
      image: {
        enableGlobalFilter: false,
        enableSorting: false,
      },
      str: {
        enableGlobalFilter: true,
      },
      long_text: {
        enableGlobalFilter: true,
      },
      float: {
        enableGlobalFilter: false,
      },
      currency: {
        enableGlobalFilter: false,
      },
      emoji: {
        enableGlobalFilter: false,
      },
      bool: {},
      date: {
        enableGlobalFilter: false,
        enableSorting: true,
      },
      datetime: {
        enableGlobalFilter: false,
        enableSorting: true,
      },
      chart: {
        enableGlobalFilter: false,
        enableSorting: false,
        enableColumnFilter: false,
        sortingFn: false, // Explicitly disable any sorting function
      },

      // going to make a separate type for launch column
      launch_status: {
        enableGlobalFilter: false,
        enableSorting: false,
      },
    };

    // this is for the launch status column
    if (columnInfo.type === "str" && columnInfo.statuses) {
      return {
        ...baseConfig,
        ...typeConfigs["launch_status"],
      };
    }

    if (filterType === "range") {
      baseConfig.filterFn = (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        const columnInfo = headerData[columnId];

        if (columnInfo && columnInfo.type === "datetime") {
          const [filterType, maxFilterString] = filterValue;

          // Handle special filter cases
          if (filterType === "empty") {
            return !value || value === "" || value === null;
          }

          if (filterType === "immediate") {
            return value === "immediate launch";
            // return value === "immediate launch" || value === "" || value === null || value === undefined;
          }

          // 1. If the filter is cleared (both min and max are undefined), show all rows.
          if (filterType === undefined && maxFilterString === undefined) {
            return true;
          }

          // 2. Filter is active. Handle cell values that should not pass an active filter.
          // `value` is from row.getValue(columnId), which should be the string from `originalRow[key].display`
          if (!value || value === "immediate launch") {
            // Covers null, undefined, "", "immediate launch"
            return false;
          }

          const cellDateValue = new Date(value);
          if (isNaN(cellDateValue.getTime())) {
            // Cell string value is not a parsable date
            return false;
          }

          // 3. Cell date is valid, filter is active. Perform comparison.
          const minDate = filterType ? new Date(filterType) : null;
          const maxDate = maxFilterString ? new Date(maxFilterString) : null;

          // Normalize filter dates for comparison
          if (minDate) minDate.setHours(0, 0, 0, 0);
          if (maxDate) maxDate.setHours(23, 59, 59, 999); // Set to end of the day

          let meetsMinCriteria = true;
          if (minDate) {
            meetsMinCriteria = cellDateValue >= minDate;
          }

          let meetsMaxCriteria = true;
          if (maxDate) {
            meetsMaxCriteria = cellDateValue <= maxDate;
          }

          return meetsMinCriteria && meetsMaxCriteria;
        } else {
          // Handle numeric range filtering (existing logic)
          const numericValue =
            typeof value === "object"
              ? parseFloat(value.display)
              : parseFloat(value);

          if (isNaN(numericValue)) return true; // This line might need similar review for numeric filters if empty/invalid should be false

          const [min, max] = filterValue;
          if (min === undefined && max === undefined) {
            // If numeric filter is cleared
            return true;
          }

          // Assuming for numeric: if filter is active, non-numeric should not pass
          // This part was `if (isNaN(numericValue)) return true;`
          // If we want to filter out NaN when filter is active:
          // if (isNaN(numericValue)) return false; // if filter is active

          let meetsMin = true;
          if (min !== undefined) {
            if (isNaN(numericValue)) return false; // Don't match if value is not a number and min is set
            meetsMin = numericValue >= min;
          }

          let meetsMax = true;
          if (max !== undefined) {
            if (isNaN(numericValue)) return false; // Don't match if value is not a number and max is set
            meetsMax = numericValue <= max;
          }

          return meetsMin && meetsMax;
        }
      };
    }

    // you know, these hacks are only here because the data from the server is not always consistent
    if (columnInfo.type === "bool") {
      baseConfig.filterFn = (row, columnId, filterValue) => {
        if (filterValue === undefined) return true;

        const cellValue = row.getValue(columnId);

        // Handle case where value is an object with display property
        const rawValue =
          typeof cellValue === "object" ? cellValue.display : cellValue;

        // Normalize the cell value to boolean
        const normalizedCellValue = (() => {
          if (rawValue === null || rawValue === undefined) return null;
          const strValue = rawValue.toString().toLowerCase();

          // Handle numeric boolean format ("0"/"1")
          if (strValue === "1") return true;
          if (strValue === "0") return false;

          // Handle string boolean format ("true"/"false")
          if (strValue === "true") return true;
          if (strValue === "false") return false;

          return null;
        })();

        return normalizedCellValue === filterValue;
      };
    }

    if (["dropdown", "rich_dropdown"].includes(columnInfo.type)) {
      baseConfig.filterFn = (row, columnId, filterValue) => {
        if (!filterValue) return true;

        const keyWithoutProp = columnId.split(".")[0]; // Get the key without .selected
        const rowData = row.original[keyWithoutProp];

        if (!rowData) return false;

        // Get selected value(s)
        const selectedValues = Array.isArray(rowData.selected)
          ? rowData.selected // Array of objects with value/display
          : rowData.selected // Single object with value/display or glid/title
          ? [rowData.selected]
          : [];

        // For rich_dropdown type
        if (columnInfo.type === "rich_dropdown") {
          return selectedValues.some(
            (selected) =>
              selected.glid
                ?.toString()
                .toLowerCase()
                .includes(filterValue.toLowerCase()) ||
              selected.title
                ?.toString()
                .toLowerCase()
                .includes(filterValue.toLowerCase()) ||
              selected.description
                ?.toString()
                .toLowerCase()
                .includes(filterValue.toLowerCase())
          );
        }

        // For regular dropdown type
        return selectedValues.some(
          (selected) =>
            selected.value
              ?.toString()
              .toLowerCase()
              .includes(filterValue.toLowerCase()) ||
            selected.display
              ?.toString()
              .toLowerCase()
              .includes(filterValue.toLowerCase())
        );
      };
    }

    return {
      ...baseConfig,
      ...(typeConfigs[columnInfo.type] || {}),
    };
  };

  if (!headerData) return [];

  // filter out the row_checkbox and row_actions columns
  // because we will show them, but not in the normal sequence, we will include them later
  const baseSortedColumns = Object.entries(headerData)
    .filter(
      ([key]) =>
        !["row_checkbox", "row_actions", "subrows", "required_fields"].includes(
          key
        )
    )
    .sort(([, a], [, b]) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .map(([key, columnInfo]) => {
      let accessor;
      if (["dropdown", "rich_dropdown"].includes(columnInfo.type)) {
        // Return both selected value and its display value
        accessor = (row) => {
          const dropdownData = row[key];
          const selectedValue = dropdownData?.selected;

          // Handle multiple selections
          if (Array.isArray(selectedValue)) {
            return selectedValue; // Already in the correct format with value/display
          }

          // Single selection - return the selected object directly
          return selectedValue; // Already contains value/display or glid/title/description/image
        };
      } else {
        accessor = `${key}.display`;
      }

      return columnHelper.accessor(accessor, getColumnConfig(key, columnInfo));
    });

  const allColumns = [
    ...(columnTypes.rowSelectionColumn ? [getRowSelectionColumn()] : []),
    ...(enableSubrows ? [getSubrowToggleColumn()] : []),
    ...baseSortedColumns,
    ...(columnTypes.actionsColumn && columnTypes.actionsColumn.length > 0
      ? [getActionColumn(columnTypes.actionsColumn, actionConfig)]
      : []),
  ];

  return allColumns;
};
